import type { AuditLogFilters, AuditLogResponse } from '@api/audit-logs-api';
import { revertAuditLog } from '@api/audit-logs-api';
import Breadcrumbs from '@components/breadcrumbs';
import ConfirmationModal from '@components/confirmation-modal';
import TableFilters from '@components/table-filters';
import { useAuditLogs } from '@hooks/use-audit-logs';
import { ChevronLeft, ChevronRight, FileText, RefreshCw, RotateCcw } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { sileo } from 'sileo';

const ENTITY_OPTIONS = [
  'Country',
  'Edition',
  'EditionCategory',
  'Nomination',
  'Participant',
  'Category',
  'User',
  'Vote',
] as const;

const ACTION_OPTIONS = ['CREATE', 'UPDATE', 'DELETE', 'REVERT'] as const;

const ACTION_COLORS: Record<string, string> = {
  CREATE: 'active',
  UPDATE: 'warning',
  DELETE: 'closed',
  REVERT: 'info',
};

const REVERTABLE_ACTIONS = new Set(['CREATE', 'UPDATE', 'DELETE']);

const NON_REVERTABLE_UPDATE_ENTITIES = new Set(['Category']);

function isRevertable(log: AuditLogResponse): boolean {
  if (!REVERTABLE_ACTIONS.has(log.action)) return false;
  if (log.action === 'UPDATE' && NON_REVERTABLE_UPDATE_ENTITIES.has(log.entity)) return false;
  return true;
}

const PAGE_SIZE = 50;

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatChanges(changes: unknown): string {
  if (changes === null || changes === undefined) return '—';
  try {
    return JSON.stringify(changes, null, 2);
  } catch {
    return String(changes);
  }
}

function getRevertDescription(log: AuditLogResponse): string {
  switch (log.action) {
    case 'DELETE':
      return `This will restore the deleted ${log.entity} (${log.entityId}) and its cascaded children.`;
    case 'CREATE':
      return `This will soft-delete the ${log.entity} (${log.entityId}) that was created.`;
    case 'UPDATE':
      return `This will restore the ${log.entity} (${log.entityId}) to its previous state.`;
    default:
      return `This will revert the ${log.action} action on ${log.entity} (${log.entityId}).`;
  }
}

function getPageRange(currentPage: number, totalPages: number): number[] {
  const MAX_VISIBLE = 7;

  if (totalPages <= MAX_VISIBLE) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: number[] = [1];

  let start = Math.max(2, currentPage - 1);
  let end = Math.min(totalPages - 1, currentPage + 1);

  if (currentPage <= 3) {
    start = 2;
    end = 5;
  } else if (currentPage >= totalPages - 2) {
    start = totalPages - 4;
    end = totalPages - 1;
  }

  if (start > 2) pages.push(-1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < totalPages - 1) pages.push(-2);

  pages.push(totalPages);
  return pages;
}

export default function AuditLogs(): React.JSX.Element {
  const [entityFilter, setEntityFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [entityIdSearch, setEntityIdSearch] = useState('');
  const [actorSearch, setActorSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [appliedFilters, setAppliedFilters] = useState<AuditLogFilters>({});
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [revertingLog, setRevertingLog] = useState<AuditLogResponse | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  const { logs, isLoading, error, refetch } = useAuditLogs(appliedFilters);

  const filteredLogs = useMemo(() => {
    if (!actionFilter) return logs;
    return logs.filter((log) => log.action === actionFilter);
  }, [logs, actionFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredLogs.length / PAGE_SIZE));
  const paginatedLogs = useMemo(
    () => filteredLogs.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filteredLogs, currentPage],
  );
  const startIndex = (currentPage - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(currentPage * PAGE_SIZE, filteredLogs.length);

  const goToPage = useCallback((page: number | ((prev: number) => number)): void => {
    setCurrentPage(page);
    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleApplyFilters = (): void => {
    const filters: AuditLogFilters = {};
    if (entityFilter) filters.entity = entityFilter;
    if (entityIdSearch.trim()) filters.entityId = entityIdSearch.trim();
    if (actorSearch.trim()) filters.actor = actorSearch.trim();
    if (fromDate) filters.from = new Date(fromDate).toISOString();
    if (toDate) filters.to = new Date(`${toDate}T23:59:59`).toISOString();

    setAppliedFilters(filters);
    setCurrentPage(1);
  };

  const handleClearFilters = (): void => {
    setEntityFilter('');
    setActionFilter('');
    setEntityIdSearch('');
    setActorSearch('');
    setFromDate('');
    setToDate('');
    setAppliedFilters({});
    setCurrentPage(1);
  };

  const handleRevert = async (log: AuditLogResponse): Promise<void> => {
    try {
      await revertAuditLog(log.id);
      sileo.success({ title: `${log.action} on ${log.entity} reverted successfully` });
      setRevertingLog(null);
      refetch();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to revert action';
      sileo.error({ title: message });
    }
  };

  const hasActiveFilters = Boolean(
    entityFilter || actionFilter || entityIdSearch || actorSearch || fromDate || toDate,
  );

  return (
    <>
      <Breadcrumbs items={[{ label: 'Editions', to: '/editions' }, { label: 'Audit Log' }]} />

      <div className="page-header">
        <div className="page-header__left">
          <h1 className="page-header__title">Audit Log</h1>
          <p className="page-header__subtitle">
            Activity history &mdash; {filteredLogs.length} entries
          </p>
        </div>
        <div className="page-header__actions">
          <button type="button" className="btn btn-secondary" onClick={refetch}>
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      <TableFilters
        rows={[
          {
            items: [
              {
                type: 'select',
                id: 'entity-filter',
                label: 'Entity',
                value: entityFilter,
                options: [
                  { value: '', label: 'All entities' },
                  ...ENTITY_OPTIONS.map((e) => ({ value: e, label: e })),
                ],
                onChange: setEntityFilter,
              },
              {
                type: 'select',
                id: 'action-filter',
                label: 'Action',
                value: actionFilter,
                options: [
                  { value: '', label: 'All actions' },
                  ...ACTION_OPTIONS.map((a) => ({ value: a, label: a })),
                ],
                onChange: setActionFilter,
              },
              {
                type: 'search',
                id: 'entity-id-search',
                label: 'Entity ID',
                placeholder: 'Search by entity ID...',
                value: entityIdSearch,
                onChange: setEntityIdSearch,
                onKeyDown: (e) => {
                  if (e.key === 'Enter') handleApplyFilters();
                },
              },
              {
                type: 'search',
                id: 'actor-search',
                label: 'Actor',
                placeholder: 'Search by actor...',
                value: actorSearch,
                onChange: setActorSearch,
                onKeyDown: (e) => {
                  if (e.key === 'Enter') handleApplyFilters();
                },
              },
            ],
          },
          {
            items: [
              {
                type: 'date',
                id: 'from-date',
                label: 'From',
                value: fromDate,
                onChange: setFromDate,
              },
              {
                type: 'date',
                id: 'to-date',
                label: 'To',
                value: toDate,
                onChange: setToDate,
              },
            ],
          },
        ]}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        showClear={hasActiveFilters}
      />

      {isLoading && (
        <div className="empty-state">
          <p>Loading audit logs...</p>
        </div>
      )}

      {error && (
        <div className="page-error" role="alert">
          <span>{error}</span>
        </div>
      )}

      {!isLoading && !error && filteredLogs.length === 0 && (
        <div className="empty-state">
          <FileText size={40} strokeWidth={1} />
          <p>No audit log entries found.</p>
        </div>
      )}

      {!isLoading && !error && filteredLogs.length > 0 && (
        <>
          <div ref={tableRef} className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Entity</th>
                  <th>Entity ID</th>
                  <th>Details</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedLogs.map((log) => (
                  <tr key={log.id}>
                    <td className="audit-table__date">{formatDateTime(log.createdAt)}</td>
                    <td>
                      <span className="audit-table__actor">{log.actor}</span>
                    </td>
                    <td>
                      <span className={`badge badge-${ACTION_COLORS[log.action] ?? 'default'}`}>
                        {log.action}
                      </span>
                    </td>
                    <td>{log.entity}</td>
                    <td>
                      <code className="audit-table__entity-id">{log.entityId}</code>
                    </td>
                    <td>
                      {log.changes ? (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}
                        >
                          {expandedRow === log.id ? 'Hide' : 'View'}
                        </button>
                      ) : (
                        <span className="audit-table__no-changes">&mdash;</span>
                      )}
                    </td>
                    <td>
                      <div className="table-actions">
                        {isRevertable(log) && (
                          <button
                            type="button"
                            className="btn btn-ghost btn-icon btn-sm"
                            data-tooltip="Revert action"
                            aria-label="Revert action"
                            onClick={() => setRevertingLog(log)}
                          >
                            <RotateCcw size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {expandedRow && (
            <div className="audit-changes">
              <div className="audit-changes__header">
                <h3 className="audit-changes__title">Changes</h3>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setExpandedRow(null)}
                >
                  Close
                </button>
              </div>
              <pre className="audit-changes__content">
                {formatChanges(filteredLogs.find((l) => l.id === expandedRow)?.changes)}
              </pre>
            </div>
          )}

          {totalPages > 1 && (
            <div className="pagination">
              <span className="pagination__info">
                {startIndex}&ndash;{endIndex} of {filteredLogs.length}
              </span>

              <div className="pagination__controls">
                <button
                  type="button"
                  className="pagination__btn"
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                  onClick={() => goToPage((p) => p - 1)}
                >
                  <ChevronLeft size={16} />
                </button>

                {getPageRange(currentPage, totalPages).map((page) =>
                  page < 0 ? (
                    <span key={page} className="pagination__ellipsis">
                      &hellip;
                    </span>
                  ) : (
                    <button
                      key={page}
                      type="button"
                      className={`pagination__btn${page === currentPage ? ' pagination__btn--active' : ''}`}
                      onClick={() => goToPage(page)}
                    >
                      {page}
                    </button>
                  ),
                )}

                <button
                  type="button"
                  className="pagination__btn"
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                  onClick={() => goToPage((p) => p + 1)}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <ConfirmationModal
        isOpen={revertingLog !== null}
        onClose={() => setRevertingLog(null)}
        onConfirm={() => {
          if (revertingLog) return handleRevert(revertingLog);
        }}
        title="Revert action"
        message={revertingLog ? getRevertDescription(revertingLog) : ''}
        confirmLabel="Revert"
        variant="danger"
      />
    </>
  );
}
